import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Chip,
  Switch,
  Textarea,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import {
  getAllUsers,
  updateUser,
  createUser,
  deleteUser,
  changeUserPassword,
} from 'common/src/api/users';
import { getAllExporters } from 'common/src/api/exporters';
import {
  getUserDescription,
  upsertUserDescription,
  deleteUserDescription,
} from 'common/src/api/user_descriptions';
import { getAllImporters } from 'common/src/api/importers';
import {
  getWhatsAppContacts,
  createWhatsAppContact,
  updateWhatsAppContact,
  deleteWhatsAppContact,
  WhatsAppContact,
} from 'common/src/api/whatsapp-contacts';
import { User, Exporter, Importer } from 'common/src/utils/types';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import Header from '../../../components/layouts/Header';
import { LoadingState, PageLayout, StatusChip } from '../../../components/ui';
import { useSectionItems } from '../../../components/layouts/sidebars/SidebarItems';
import BasicPermissionsPicker from '../../../components/users/BasicPermissionsPicker';
import { buildAvailablePermissions } from '../../../utils/user-permissions';

type UserType = 'exporter' | 'importer';

const SuperadminPeople = () => {
  const { session, userProfile } = useUserStore();
  const sidebarItems = useSectionItems(false);
  // Permisos que se pueden dar a un usuario basic de una exportadora
  // (Volumen Exportación solo si está suscrita a ASOEX).
  const permissionsFor = (exporter?: Exporter | null) =>
    buildAvailablePermissions(sidebarItems, {
      hasVolume: exporter?.industry_volume_access === 'full',
    });
  // Solo los permisos marcados, como los guarda create-user / updateUser.
  const enabledPermissions = (exporter: Exporter | null | undefined, keys: string[]) =>
    permissionsFor(exporter)
      .filter((p) => keys.includes(p.sidebarItemKey))
      .map((p) => ({ sidebarItemKey: p.sidebarItemKey, enabled: true, title: p.title }));
  const [newPermissionKeys, setNewPermissionKeys] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [importers, setImporters] = useState<Importer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<UserType>('exporter');
  const [newUserType, setNewUserType] = useState<UserType | null>(null);
  const [newUserStep, setNewUserStep] = useState<1 | 2>(1);
  const [newUser, setNewUser] = useState<Partial<User>>({
    email: '',
    first_name: '',
    last_name: '',
    roles: [],
    exporter: undefined,
    importer: undefined,
    password: '',
  });
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedExporter, setSelectedExporter] = useState<string>('all');
  const [selectedImporter, setSelectedImporter] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [filterUserType, setFilterUserType] = useState<'all' | 'exporter' | 'importer'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showArchived, setShowArchived] = useState(false);
  const {
    isOpen: isPasswordOpen,
    onOpen: onPasswordOpen,
    onClose: onPasswordClose,
  } = useDisclosure();
  const [newPassword, setNewPassword] = useState('');

  // WhatsApp contacts state
  const [waContacts, setWaContacts] = useState<Map<number, WhatsAppContact>>(new Map());
  const {
    isOpen: isWhatsAppOpen,
    onOpen: onWhatsAppOpen,
    onClose: onWhatsAppClose,
  } = useDisclosure();
  const [waPhone, setWaPhone] = useState('');
  const [waIsActive, setWaIsActive] = useState(true);

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const fetchUsers = async (archived = false) => {
    setLoading(true);
    try {
      const fetchedUsers = await getAllUsers({ archived });
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchExporters = async () => {
    try {
      const fetchedExporters = await getAllExporters();
      setExporters(fetchedExporters);
    } catch (error) {
      console.error('Error fetching exporters:', error);
      toast.error('Failed to fetch exporters');
    }
  };

  const fetchImporters = async () => {
    try {
      const fetchedImporters = await getAllImporters();
      setImporters(fetchedImporters);
    } catch (error) {
      console.error('Error fetching importers:', error);
      toast.error('Failed to fetch importers');
    }
  };

  const fetchWaContacts = async () => {
    try {
      const contacts = await getWhatsAppContacts();
      const map = new Map<number, WhatsAppContact>();
      contacts.forEach((c) => map.set(c.user_id, c));
      setWaContacts(map);
    } catch (error) {
      console.error('Error fetching WhatsApp contacts:', error);
    }
  };

  useEffect(() => {
    fetchUsers(false);
    fetchExporters();
    fetchImporters();
    fetchWaContacts();
  }, []);

  const [agentDescription, setAgentDescription] = useState<string>('');

  const handleEdit = (user: User) => {
    setAgentDescription('');
    getUserDescription(user.id)
      .then((d) => setAgentDescription(d ?? ''))
      .catch(() => setAgentDescription(''));
    setSelectedUser(user);
    // Determine user type based on existing association
    if (user.importer_id || user.importer) {
      setSelectedUserType('importer');
    } else {
      setSelectedUserType('exporter');
    }
    onEditOpen();
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    onDeleteOpen();
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;
    setLoadingAction(true);
    try {
      if (!userProfile?.roles.includes('superadmin')) {
        throw new Error('Only superadmins can update users');
      }
      const userToUpdate = { ...selectedUser };
      delete userToUpdate.exporter;
      delete userToUpdate.importer;

      // Clear the opposite type when switching
      if (selectedUserType === 'exporter') {
        userToUpdate.importer_id = undefined;
      } else {
        userToUpdate.exporter_id = undefined;
      }
      // Usuario basic de exportadora: se guardan los permisos marcados.
      if (selectedUserType === 'exporter' && userToUpdate.roles?.includes('basic')) {
        userToUpdate.permissions = enabledPermissions(
          exporters.find((e) => e.id === userToUpdate.exporter_id),
          (selectedUser.permissions ?? []).filter((p) => p.enabled).map((p) => p.sidebarItemKey)
        );
      }

      await updateUser(selectedUser.id, userToUpdate, session);
      const trimmed = agentDescription.trim();
      if (trimmed) {
        await upsertUserDescription(selectedUser.id, trimmed, userProfile?.id);
      } else {
        await deleteUserDescription(selectedUser.id);
      }
      toast.success('User updated successfully');
      onEditClose();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleNewSubmit = async () => {
    setLoadingAction(true);
    try {
      if (!userProfile?.roles.includes('superadmin')) {
        throw new Error('Only superadmins can create users');
      }

      // Prepare user data based on type
      const userToCreate = { ...newUser };
      if (newUserType === 'exporter') {
        delete userToCreate.importer;
        userToCreate.importer_id = undefined;
      } else {
        delete userToCreate.exporter;
        userToCreate.exporter_id = undefined;
      }

      userToCreate.permissions =
        newUserType === 'exporter' && userToCreate.roles?.includes('basic')
          ? enabledPermissions(newUser.exporter, newPermissionKeys)
          : [];

      const response = await createUser(userToCreate, session);
      if (response) {
        toast.success('Usuario creado con éxito');
        onNewClose();
        setNewUser({
          email: '',
          first_name: '',
          last_name: '',
          roles: [],
          exporter: undefined,
          importer: undefined,
        });
        setNewUserType(null);
        setNewUserStep(1);
        setNewPermissionKeys([]);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create new user');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    setLoadingAction(true);
    try {
      if (!userProfile?.roles.includes('superadmin')) {
        throw new Error('Only superadmins can delete users');
      }
      await deleteUser(selectedUser.id, session);
      toast.success('User deleted successfully');
      onDeleteClose();
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setLoadingAction(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!selectedUser?.auth_id || !newPassword) return;
    setLoadingAction(true);
    try {
      if (!userProfile?.roles.includes('superadmin')) {
        throw new Error('Only superadmins can change passwords');
      }
      const response = await changeUserPassword(
        selectedUser.auth_id,
        newPassword,
        session
      );
      console.log(response);
      if (response) {
        toast.success('Password changed successfully');
        onPasswordClose();
        setNewPassword('');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleWhatsAppOpen = (user: User) => {
    setSelectedUser(user);
    const contact = waContacts.get(user.id);
    if (contact) {
      setWaPhone(contact.phone_number);
      setWaIsActive(contact.is_active);
    } else {
      setWaPhone('');
      setWaIsActive(true);
    }
    onWhatsAppOpen();
  };

  const handleWhatsAppSave = async () => {
    if (!selectedUser || !waPhone.trim()) return;
    setLoadingAction(true);
    try {
      const existing = waContacts.get(selectedUser.id);
      if (existing) {
        await updateWhatsAppContact(existing.id, {
          phone_number: waPhone.trim(),
          is_active: waIsActive,
        });
        toast.success('Contacto WhatsApp actualizado');
      } else {
        await createWhatsAppContact({
          user_id: selectedUser.id,
          phone_number: waPhone.trim(),
        });
        toast.success('Contacto WhatsApp creado');
      }
      await fetchWaContacts();
      onWhatsAppClose();
    } catch (error) {
      console.error('Error saving WhatsApp contact:', error);
      toast.error('Error al guardar contacto WhatsApp');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleWhatsAppDelete = async () => {
    if (!selectedUser) return;
    const existing = waContacts.get(selectedUser.id);
    if (!existing) return;
    setLoadingAction(true);
    try {
      await deleteWhatsAppContact(existing.id);
      toast.success('Contacto WhatsApp eliminado');
      await fetchWaContacts();
      onWhatsAppClose();
    } catch (error) {
      console.error('Error deleting WhatsApp contact:', error);
      toast.error('Error al eliminar contacto WhatsApp');
    } finally {
      setLoadingAction(false);
    }
  };

  const columns = [
    { name: 'ID', uid: 'id' },
    { name: 'Email', uid: 'email' },
    { name: 'First Name', uid: 'first_name' },
    { name: 'Last Name', uid: 'last_name' },
    { name: 'Roles', uid: 'roles' },
    { name: 'Exportador', uid: 'exporter' },
    { name: 'Importador', uid: 'importer' },
    { name: 'Demo', uid: 'demo' },
    { name: 'Estado', uid: 'status' },
    { name: 'Actions', uid: 'actions' },
  ];

  const renderCell = (user: User, columnKey: React.Key) => {
    switch (columnKey) {
      case 'roles':
        return (
          <div className='flex flex-wrap gap-1'>
            {user.roles.map((role, index) => (
              <Chip key={index} size='sm' variant='flat'>
                {role}
              </Chip>
            ))}
          </div>
        );
      case 'exporter':
        return user.exporter ? user.exporter.name : '-';
      case 'importer':
        return user.importer ? user.importer.name : '-';
      case 'demo': {
        if (!user.demo_access_until) return '-';
        const isExpired = new Date(user.demo_access_until + 'T23:59:59') < new Date();
        const formatted = new Date(user.demo_access_until + 'T00:00:00').toLocaleDateString('es-CL');
        return (
          <StatusChip tone={isExpired ? 'danger' : 'info'}>
            {isExpired ? `Expiró ${formatted}` : `Hasta ${formatted}`}
          </StatusChip>
        );
      }
      case 'status':
        return user.deleted_at ? (
          <Chip size='sm' color='default' variant='flat'>
            Archivado {new Date(user.deleted_at).toLocaleDateString('es-CL')}
          </Chip>
        ) : (
          <Chip size='sm' color='success' variant='flat'>
            Activo
          </Chip>
        );
      case 'actions': {
        if (user.deleted_at) {
          return <span className='text-sm text-default-500'>Solo lectura</span>;
        }
        const waContact = (user as any)._waContact as WhatsAppContact | undefined;
        const waActive = waContact?.is_active ?? false;
        return (
          <div className='flex items-center gap-2'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleWhatsAppOpen(user)}
              title={waContact ? (waActive ? 'WhatsApp activo' : 'WhatsApp inactivo') : 'Sin WhatsApp'}
            >
              <Icon icon='tabler:brand-whatsapp' className={waContact && waActive ? 'text-green-500' : 'text-default-300'} />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleEdit(user)}
            >
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => {
                setSelectedUser(user);
                onPasswordOpen();
              }}
            >
              <Icon icon='tabler:lock' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              onClick={() => handleDelete(user)}
            >
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      }
      default:
        return user[columnKey as keyof User];
    }
  };

  const filteredUsers = users
    .filter((user) => {
      // User type filter
      const userTypeMatch =
        filterUserType === 'all' ||
        (filterUserType === 'exporter' && (user.exporter_id || user.exporter)) ||
        (filterUserType === 'importer' && (user.importer_id || user.importer));

      const exporterMatch =
        selectedExporter === 'all' ||
        (user.exporter && user.exporter.id.toString() === selectedExporter);

      const importerMatch =
        selectedImporter === 'all' ||
        (user.importer && user.importer.id.toString() === selectedImporter);

      const roleMatch =
        selectedRole === 'all' || user.roles.includes(selectedRole);

      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const searchMatch =
        !searchQuery ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.exporter?.name?.toLowerCase().includes(searchLower) ||
        user.importer?.name?.toLowerCase().includes(searchLower) ||
        user.roles.some((role) => role.toLowerCase().includes(searchLower));

      return userTypeMatch && exporterMatch && importerMatch && roleMatch && searchMatch;
    })
    .sort((a, b) => {
      // Sort alphabetically by first name, then last name
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((user) => ({
      ...user,
      _waContact: waContacts.get(user.id),
    }));

  return (
    <PageLayout>
      <Header
        title='Usuarios'
        actions={
          <Button
            color='primary'
            onClick={onNewOpen}
            isDisabled={!userProfile?.roles.includes('superadmin')}
          >
            Agregar usuario
          </Button>
        }
      />
      <div className='flex flex-col gap-4 mb-4'>

        <div className='flex flex-wrap items-center gap-4'>
          <Switch
            isSelected={showArchived}
            onValueChange={(archived) => {
              setShowArchived(archived);
              fetchUsers(archived);
            }}
            size='sm'
          >
            Ver archivados
          </Switch>
          <Input
            placeholder='Buscar por nombre, email, exportadora, importador o rol...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='flex-1 min-w-[300px]'
            isClearable
            onClear={() => setSearchQuery('')}
          />

          <Select
            placeholder='Tipo de usuario'
            value={filterUserType}
            onChange={(e) => setFilterUserType(e.target.value as 'all' | 'exporter' | 'importer')}
            defaultSelectedKeys={['all']}
            className='w-48'
          >
            <SelectItem key='all' value='all'>
              Todos los tipos
            </SelectItem>
            <SelectItem key='exporter' value='exporter'>
              Exportadores
            </SelectItem>
            <SelectItem key='importer' value='importer'>
              Importadores
            </SelectItem>
          </Select>

          <Select
            placeholder='Filter by Role'
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            defaultSelectedKeys={['all']}
            className='w-48'
          >
            <SelectItem key='all' value='all'>
              Todos los roles
            </SelectItem>
            <SelectItem key='superadmin' value='superadmin'>
              Superadmin
            </SelectItem>
            <SelectItem key='admin' value='admin'>
              Admin
            </SelectItem>
            <SelectItem key='document_helper' value='document_helper'>
              Document Helper
            </SelectItem>
            <SelectItem key='basic' value='basic'>
              Basic
            </SelectItem>
          </Select>

          {filterUserType !== 'importer' && (
            <Autocomplete
              placeholder='Buscar exportadora...'
              label='Exportadora'
              defaultSelectedKey='all'
              selectedKey={selectedExporter}
              onSelectionChange={(key) =>
                setSelectedExporter((key as string) || 'all')
              }
              className='w-48'
            >
              <AutocompleteItem key='all' value='all'>
                Todas las exportadoras
              </AutocompleteItem>
              {exporters.map((exporter) => (
                <AutocompleteItem
                  key={exporter.id}
                  value={exporter.id.toString()}
                >
                  {exporter.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>
          )}

          {filterUserType !== 'exporter' && (
            <Select
              placeholder='Filter by Importer'
              value={selectedImporter}
              onChange={(e) => setSelectedImporter(e.target.value)}
              defaultSelectedKeys={['all']}
              className='w-48'
            >
              <SelectItem key='all' value='all'>
                Todos los importadores
              </SelectItem>
              {importers.map((importer) => (
                <SelectItem key={importer.id} value={importer.id.toString()}>
                  {importer.name}
                </SelectItem>
              ))}
            </Select>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : (
        <Table removeWrapper aria-label='Users table'>
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn key={column.uid}>{column.name}</TableColumn>
            )}
          </TableHeader>
          <TableBody items={filteredUsers}>
            {(item) => (
              <TableRow key={`${item.id}-${(item as any)._waContact?.is_active ?? 'none'}`}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalContent>
          <ModalHeader>Editar Usuario</ModalHeader>
          <ModalBody>
            <Select
              label='Tipo de Usuario'
              placeholder='Seleccionar tipo'
              selectedKeys={[selectedUserType]}
              onSelectionChange={(keys) => {
                const type = Array.from(keys)[0] as UserType;
                setSelectedUserType(type);
                // Clear both when switching type
                if (selectedUser) {
                  setSelectedUser({
                    ...selectedUser,
                    exporter_id: undefined,
                    importer_id: undefined,
                  });
                }
              }}
            >
              <SelectItem key='exporter' value='exporter'>
                Exportador
              </SelectItem>
              <SelectItem key='importer' value='importer'>
                Importador
              </SelectItem>
            </Select>
            <Input
              label='Email'
              value={selectedUser?.email || ''}
              onChange={(e) =>
                setSelectedUser({
                  ...selectedUser!,
                  email: e.target.value,
                })
              }
            />
            <Input
              label='First Name'
              value={selectedUser?.first_name || ''}
              onChange={(e) =>
                setSelectedUser({
                  ...selectedUser!,
                  first_name: e.target.value,
                })
              }
            />
            <Input
              label='Last Name'
              value={selectedUser?.last_name || ''}
              onChange={(e) =>
                setSelectedUser({
                  ...selectedUser!,
                  last_name: e.target.value,
                })
              }
            />
            <Textarea
              label='Descripción para el agente (interna)'
              placeholder='Quién es, qué hace y qué le interesa. El agente de IA la usa como contexto. El usuario NO la ve. Ej: "Gerente comercial, le interesa el mercado semana a semana, prefiere números concretos."'
              description='Solo visible para superadmins y para el agente.'
              maxLength={1000}
              minRows={3}
              value={agentDescription}
              onValueChange={setAgentDescription}
            />
            <Select
              label='Roles'
              selectionMode='multiple'
              placeholder='Select roles'
              selectedKeys={selectedUser?.roles || []}
              onSelectionChange={(keys) =>
                setSelectedUser({
                  ...selectedUser!,
                  roles: Array.from(keys) as string[],
                })
              }
            >
              <SelectItem key='admin' value='admin'>
                Admin
              </SelectItem>
              <SelectItem key='basic' value='basic'>
                Basic
              </SelectItem>
              <SelectItem key='document_helper' value='document_helper'>
                Document Helper
              </SelectItem>
              <SelectItem key='superadmin' value='superadmin'>
                Superadmin
              </SelectItem>
            </Select>
            {selectedUserType === 'exporter' ? (
              <Autocomplete
                label='Exportadora'
                placeholder='Buscar exportadora...'
                selectedKey={
                  selectedUser?.exporter_id
                    ? selectedUser.exporter_id.toString()
                    : null
                }
                onSelectionChange={(key) => {
                  if (key) {
                    setSelectedUser({
                      ...selectedUser!,
                      exporter_id: parseInt(key as string, 10),
                    });
                  }
                }}
              >
                {exporters.map((exporter) => (
                  <AutocompleteItem
                    key={exporter.id}
                    value={exporter.id.toString()}
                  >
                    {exporter.name}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
            ) : (
              <Select
                label='Importador'
                placeholder='Seleccionar importador'
                selectedKeys={
                  selectedUser?.importer_id
                    ? [selectedUser.importer_id.toString()]
                    : []
                }
                onSelectionChange={(keys) => {
                  const importerId = Array.from(keys)[0] as string;
                  setSelectedUser({
                    ...selectedUser!,
                    importer_id: parseInt(importerId, 10),
                  });
                }}
              >
                {importers.map((importer) => (
                  <SelectItem key={importer.id} value={importer.id.toString()}>
                    {importer.name}
                  </SelectItem>
                ))}
              </Select>
            )}
            {selectedUserType === 'exporter' && selectedUser?.roles?.includes('basic') && (
              <BasicPermissionsPicker
                available={permissionsFor(exporters.find((e) => e.id === selectedUser.exporter_id))}
                selected={(selectedUser.permissions ?? [])
                  .filter((p) => p.enabled)
                  .map((p) => p.sidebarItemKey)}
                onChange={(keys) =>
                  setSelectedUser({
                    ...selectedUser,
                    permissions: keys.map((key) => ({ sidebarItemKey: key, enabled: true, title: key })),
                  })
                }
              />
            )}
            <Input
              label='Acceso demo hasta'
              type='date'
              placeholder=' '
              value={selectedUser?.demo_access_until || ''}
              onChange={(e) =>
                setSelectedUser({
                  ...selectedUser!,
                  demo_access_until: e.target.value || '',
                })
              }
              description='Dejar vacío si no es cuenta demo'
              isClearable
              onClear={() =>
                setSelectedUser({
                  ...selectedUser!,
                  demo_access_until: '',
                })
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onClick={onEditClose}
              disabled={loadingAction}
              isLoading={loadingAction}
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleEditSubmit}
              disabled={loadingAction}
              isLoading={loadingAction}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalBody>
            Are you sure you want to delete {selectedUser?.email}?
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onClick={onDeleteClose}
              disabled={loadingAction}
              isLoading={loadingAction}
            >
              Cancel
            </Button>
            <Button
              color='danger'
              onClick={handleDeleteConfirm}
              disabled={loadingAction}
              isLoading={loadingAction}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isNewOpen}
        onClose={() => {
          onNewClose();
          setNewUserStep(1);
          setNewUserType(null);
        }}
        size={newUserStep === 1 ? 'md' : 'lg'}
      >
        <ModalContent>
          {newUserStep === 1 ? (
            <>
              <ModalHeader className='flex flex-col gap-1'>
                <span>Nuevo Usuario</span>
                <span className='text-sm font-normal text-default-500'>
                  Selecciona el tipo de usuario que deseas crear
                </span>
              </ModalHeader>
              <ModalBody className='py-6'>
                <div className='grid grid-cols-2 gap-4'>
                  <button
                    onClick={() => {
                      setNewUserType('exporter');
                      setNewUserStep(2);
                    }}
                    className='group flex flex-col items-center justify-center gap-4 p-6 rounded-xl border-2 border-default-200 hover:border-brand-500 hover:bg-brand-50 transition-all duration-200 cursor-pointer'
                  >
                    <div className='w-16 h-16 rounded-full bg-brand-100 group-hover:bg-brand-200 flex items-center justify-center transition-colors'>
                      <Icon
                        icon='tabler:building-factory-2'
                        className='w-8 h-8 text-brand-600'
                      />
                    </div>
                    <div className='text-center'>
                      <p className='font-semibold text-lg text-default-700 group-hover:text-brand-700'>
                        Exportador
                      </p>
                      <p className='text-sm text-default-400'>
                        Usuario de exportadora
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setNewUserType('importer');
                      setNewUserStep(2);
                    }}
                    className='group flex flex-col items-center justify-center gap-4 p-6 rounded-xl border-2 border-default-200 hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 cursor-pointer'
                  >
                    <div className='w-16 h-16 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center transition-colors'>
                      <Icon
                        icon='tabler:building-store'
                        className='w-8 h-8 text-blue-600'
                      />
                    </div>
                    <div className='text-center'>
                      <p className='font-semibold text-lg text-default-700 group-hover:text-blue-700'>
                        Importador
                      </p>
                      <p className='text-sm text-default-400'>
                        Usuario de importadora
                      </p>
                    </div>
                  </button>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='light'
                  onClick={() => {
                    onNewClose();
                    setNewUserStep(1);
                    setNewUserType(null);
                  }}
                >
                  Cancelar
                </Button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalHeader className='flex flex-col gap-1'>
                <div className='flex items-center gap-2'>
                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    onClick={() => {
                      setNewUserStep(1);
                      setNewUserType(null);
                    }}
                  >
                    <Icon icon='tabler:arrow-left' className='w-4 h-4' />
                  </Button>
                  <span>
                    Nuevo {newUserType === 'exporter' ? 'Exportador' : 'Importador'}
                  </span>
                </div>
              </ModalHeader>
              <ModalBody>
                <div className='flex flex-col gap-4'>
                  <Input
                    label='Email'
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    isRequired
                  />
                  <div className='grid grid-cols-2 gap-4'>
                    <Input
                      label='Nombre'
                      value={newUser.first_name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, first_name: e.target.value })
                      }
                    />
                    <Input
                      label='Apellido'
                      value={newUser.last_name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, last_name: e.target.value })
                      }
                    />
                  </div>
                  <Input
                    label='Contraseña'
                    value={newUser.password || ''}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        password: e.target.value,
                      } as Partial<User> & { password: string })
                    }
                    isRequired
                  />
                  <Select
                    label='Roles'
                    selectionMode='multiple'
                    placeholder='Seleccionar roles'
                    selectedKeys={newUser.roles}
                    onSelectionChange={(keys) =>
                      setNewUser({
                        ...newUser,
                        roles: Array.from(keys) as string[],
                      })
                    }
                  >
                    <SelectItem key='admin' value='admin'>
                      Admin
                    </SelectItem>
                    <SelectItem key='basic' value='basic'>
                      Basic
                    </SelectItem>
                    <SelectItem key='document_helper' value='document_helper'>
                      Document Helper
                    </SelectItem>
                    <SelectItem key='superadmin' value='superadmin'>
                      Superadmin
                    </SelectItem>
                  </Select>
                  {newUserType === 'exporter' ? (
                    <Autocomplete
                      label='Exportadora'
                      placeholder='Buscar exportadora...'
                      selectedKey={
                        newUser.exporter ? newUser.exporter.id.toString() : null
                      }
                      onSelectionChange={(key) => {
                        if (key) {
                          const selected = exporters.find(
                            (e) => e.id.toString() === key
                          );
                          setNewUser({
                            ...newUser,
                            exporter: selected || undefined,
                          });
                        }
                      }}
                      isRequired
                    >
                      {exporters.map((exporter) => (
                        <AutocompleteItem
                          key={exporter.id}
                          value={exporter.id.toString()}
                        >
                          {exporter.name}
                        </AutocompleteItem>
                      ))}
                    </Autocomplete>
                  ) : (
                    <Select
                      label='Importador'
                      placeholder='Seleccionar importador'
                      selectedKeys={
                        newUser.importer ? [newUser.importer.id.toString()] : []
                      }
                      onSelectionChange={(keys) => {
                        const importerId = Array.from(keys)[0] as string;
                        const selected = importers.find(
                          (i) => i.id.toString() === importerId
                        );
                        setNewUser({
                          ...newUser,
                          importer: selected || undefined,
                        });
                      }}
                      isRequired
                    >
                      {importers.map((importer) => (
                        <SelectItem key={importer.id} value={importer.id.toString()}>
                          {importer.name}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                  {newUserType === 'exporter' && newUser.roles?.includes('basic') && newUser.exporter && (
                    <BasicPermissionsPicker
                      available={permissionsFor(newUser.exporter)}
                      selected={newPermissionKeys}
                      onChange={setNewPermissionKeys}
                    />
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='light'
                  onClick={() => {
                    setNewUserStep(1);
                    setNewUserType(null);
                  }}
                >
                  Volver
                </Button>
                <Button
                  color='primary'
                  onClick={handleNewSubmit}
                  disabled={loadingAction}
                  isLoading={loadingAction}
                >
                  Crear Usuario
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal isOpen={isPasswordOpen} onClose={onPasswordClose}>
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalBody>
            <p className='mb-4'>
              Change password for user: {selectedUser?.email}
            </p>
            <Input
              label='New Password'
              type='text'
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onClick={() => {
                onPasswordClose();
                setNewPassword('');
              }}
              disabled={loadingAction}
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handlePasswordChange}
              disabled={loadingAction}
              isLoading={loadingAction}
            >
              Change Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isWhatsAppOpen} onClose={onWhatsAppClose}>
        <ModalContent>
          <ModalHeader>
            WhatsApp — {selectedUser?.first_name} {selectedUser?.last_name}
          </ModalHeader>
          <ModalBody>
            <Input
              label='Número de teléfono'
              placeholder='+56912345678'
              value={waPhone}
              onChange={(e) => setWaPhone(e.target.value)}
            />
            {waContacts.get(selectedUser?.id ?? -1) && (
              <div className='flex items-center justify-between'>
                <span className='text-sm'>Acceso activo</span>
                <Switch
                  isSelected={waIsActive}
                  onValueChange={setWaIsActive}
                  color='success'
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {waContacts.get(selectedUser?.id ?? -1) && (
              <Button
                color='danger'
                variant='light'
                onClick={handleWhatsAppDelete}
                disabled={loadingAction}
                isLoading={loadingAction}
                className='mr-auto'
              >
                Eliminar
              </Button>
            )}
            <Button
              variant='light'
              onClick={onWhatsAppClose}
              disabled={loadingAction}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleWhatsAppSave}
              disabled={loadingAction || !waPhone.trim()}
              isLoading={loadingAction}
            >
              {waContacts.get(selectedUser?.id ?? -1) ? 'Guardar' : 'Activar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminPeople;
